import type {
  ColumnDef,
  PaginationState,
  Row,
  TableOptions,
} from "@tanstack/react-table";
import type { ReactNode } from "react";

import { Icon, Typo } from "@pepperstone/pepper-ui-kit";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TranslatedContent } from "./types";

import { useIsRtl } from "../hooks/useIsRtl";
import { TableState } from "../TableState";
import { Pagination } from "./Pagination";
import { TableMobileSkeletonLoader } from "./TableMobileSkeletonLoader";

export type TableMobileRowProps = {
  label: string;
  value: string | number | ReactNode;
  valueClassName?: string;
};

type TableMobileHeaderProps = {
  name?: string;
  status?: ReactNode;
  email?: string;
  clientId?: TableMobileRowProps;
  subHeaderRows?: TableMobileRowProps[];
};

export const TableMobileHeader = ({
  name,
  status,
  email,
  clientId,
  subHeaderRows,
}: TableMobileHeaderProps) => {
  const canViewPII = !clientId;

  return (
    <>
      {canViewPII && (email || name) && (
        <div className="pb-3">
          <div className="flex min-w-full justify-between">
            <div className="flex-1 text-text-primary">
              {name && name != "REDACTED" && (
                <div className="pb-1">
                  <Typo elem="p" size="md-mobile" type="label">
                    {name}
                  </Typo>
                </div>
              )}
              {email && email != "REDACTED" && (
                <Typo
                  elem="p"
                  size="sm"
                  type={`${name == "REDACTED" ? "label" : "body"}`}
                >
                  {email}
                </Typo>
              )}
            </div>
            {status && (
              <div className="flex items-center justify-end">{status}</div>
            )}
          </div>
        </div>
      )}
      {!canViewPII && (
        <div className="pb-3">
          <div className="flex min-w-full justify-between">
            <div className="flex-1 text-text-primary">
              <div className="pb-1">
                <Typo elem="p" size="sm" type="body">
                  {clientId?.label || ""}
                </Typo>
              </div>
              {clientId && (
                <Typo elem="p" size="md-mobile" type="label">
                  {clientId.value}
                </Typo>
              )}
            </div>
            {status && (
              <div className="flex items-center justify-end">{status}</div>
            )}
          </div>
        </div>
      )}
      {subHeaderRows && subHeaderRows.length > 0 && (
        <div className="border-border-primary-base">
          {subHeaderRows.map((row, index) => (
            <div
              key={index}
              className="flex justify-between pb-1 pt-1 last:pb-0"
            >
              <div className="flex-1 text-text-secondary">
                <Typo elem="p" size="sm" type="body">
                  {row.label}
                </Typo>
              </div>
              <div className="flex-1 text-end text-text-primary">
                <Typo elem="p" size="sm" type="label">
                  {row.value}
                </Typo>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const TableMobileRow = ({
  label,
  value,
  valueClassName,
}: TableMobileRowProps) => (
  <div className="flex justify-between border-b border-border-primary-base py-2 last:border-b-0">
    <div className="flex-1 text-text-secondary">
      <Typo elem="p" size="sm" type="body">
        {label}
      </Typo>
    </div>
    <div className={clsx("flex-1 text-end text-text-primary", valueClassName)}>
      <Typo elem="p" size="sm" type="body">
        {value}
      </Typo>
    </div>
  </div>
);

type TableMobileProps<T> = {
  data: T[];
  pageSize?: number;
  columns: ColumnDef<T, any>[];
  showPagination?: boolean;
  onRetry?: () => void;
  cardHeader?: (record: T) => React.ReactNode;
  cardBody: (record: T) => React.ReactNode;
  globalFilter?: any;
  columnFilters?: any;
  translatedContent?: TranslatedContent;
  expandable?: boolean;
  scaId?: string;
  onFilterChange?: (rows: T[]) => void;
  enableServerSidePagination?: boolean;
  pageCount?: number;
  totalCount?: number;
  state?: { pagination?: PaginationState };
  onPaginationChange?: (updater: any) => void;
  isLoading?: boolean;
  isError?: boolean;
  errorComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  cellContainerClassName?: string;
  // Height of the spacer between the last row and pagination. Defaults to the
  // existing 8px (`h-2`) so other consumers are unaffected by a per-page override.
  paginationSpacingClassName?: string;
  // Optional card highlighting: when `getRowId(row) === highlightedRowId`, the
  // card is scrolled into view and briefly blinks. No-op unless both are set.
  highlightedRowId?: string;
  getRowId?: (row: T) => string;
} & Pick<
  TableOptions<T>,
  | "getFilteredRowModel"
  | "onGlobalFilterChange"
  | "globalFilterFn"
  | "filterFns"
  | "onColumnFiltersChange"
>;

export const TableMobile = <T,>({
  data,
  pageSize = 10,
  showPagination = true,
  onRetry,
  cardHeader,
  cardBody,
  globalFilter,
  columns,
  columnFilters,
  translatedContent,
  expandable = true,
  scaId: _scaId,
  onFilterChange,
  enableServerSidePagination = false,
  pageCount,
  totalCount,
  state,
  onPaginationChange,
  isLoading = false,
  isError = false,
  errorComponent,
  emptyComponent,
  cellContainerClassName,
  paginationSpacingClassName = "h-2",
  highlightedRowId,
  getRowId,
  ...filterProps
}: TableMobileProps<T>) => {
  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: showPagination ? pageSize : 100, // Adding 100 as the max table size of non paginated data
  });

  const pagination =
    enableServerSidePagination && state?.pagination
      ? state.pagination
      : localPagination;
  const setPagination =
    enableServerSidePagination && onPaginationChange
      ? onPaginationChange
      : setLocalPagination;

  const isRtl = useIsRtl();
  const { t } = useTranslation();
  // oxlint-disable-next-line react/incompatible-library -- TanStack Table's own API, not memoized by this component
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: { pagination, globalFilter, columnFilters },
    manualPagination: enableServerSidePagination,
    pageCount: enableServerSidePagination ? pageCount : undefined,
    ...filterProps,
  });

  const toggleExpanded = (row: Row<T>) => {
    table.toggleAllRowsExpanded(false);
    if (!row.getIsExpanded()) {
      row.toggleExpanded();
    }
  };

  const rows = table.getRowModel().rows;

  const isRowHighlighted = (row: Row<T>) =>
    !!highlightedRowId &&
    !!getRowId &&
    getRowId(row.original) === highlightedRowId;

  // Key of the highlighted card, or undefined when it isn't rendered.
  const highlightedRowKey = rows.find(isRowHighlighted)?.id;

  // Scroll the highlighted card into view. Depending on highlightedRowKey (not
  // just the id) means the effect also fires once the card actually renders —
  // e.g. when the id is set before the page's data has loaded.
  const highlightedCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (highlightedRowId && highlightedCardRef.current) {
      highlightedCardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightedRowId, highlightedRowKey]);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(
        table.getFilteredRowModel().rows.map((row) => row.original),
      );
    }
  }, [onFilterChange, globalFilter, table]);

  useEffect(() => {
    table.toggleAllRowsExpanded(false);
  }, [rows, table]);

  if (isLoading) {
    return <TableMobileSkeletonLoader />;
  }

  // Default error/empty states, used whenever a consumer doesn't supply its
  // own `errorComponent`/`emptyComponent` — mirrors `TableDesktop`'s defaults
  // so both surfaces share the same illustrated design. Error copy comes from
  // the shared `clientsPage_table_error_*` keys (generic across every
  // table), so it can always render. Empty copy is inherently per-page, so it
  // only comes from `translatedContent` — if that's incomplete, no default is
  // shown rather than rendering a `TableState` with blank text.
  const defaultErrorState = (
    <TableState
      variant="error"
      title={t("clientsPage_table_error_title")}
      description={t("clientsPage_table_error_text")}
      actionLabel={t("clientsPage_data_error_action_text")}
      onAction={onRetry}
    />
  );

  // Mirrors the pre-existing `NoRecordsFound` heuristic: a non-empty `data`
  // with zero visible rows means the table itself has data but the current
  // filters produced no matches, vs. a genuinely empty `data` set.
  const isFilteredEmpty = data.length > 0;
  const emptyDescription = isFilteredEmpty
    ? translatedContent?.noResultsFoundText
    : translatedContent?.noDataFoundText;
  const defaultEmptyState =
    translatedContent?.noDataFoundTitle && emptyDescription ? (
      <TableState
        variant="empty"
        title={translatedContent.noDataFoundTitle}
        description={emptyDescription}
      />
    ) : undefined;

  if (isError) {
    if (errorComponent) {
      return errorComponent;
    }

    return (
      <div
        className="flex w-full flex-col gap-[40px]"
        data-testid="mobile-table"
      >
        <div className="mt-[48px] px-[24px]">{defaultErrorState}</div>
        {showPagination && table.getPageCount() > 1 && (
          <>
            <Pagination
              table={table}
              pageSize={pageSize}
              labelForShowing={translatedContent?.paginationLabelForShowing}
              labelForOf={translatedContent?.paginationLabelForOf}
              isRightToLeft={isRtl}
              totalCount={totalCount}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div data-testid="mobile-table">
      {rows.length > 0
        ? expandable && cardHeader
          ? rows.map((row) => (
              <div
                className={`my-2 min-w-full ${isRowHighlighted(row) ? "animate-row-highlight rounded-lg" : ""}`}
                key={row.id}
                ref={isRowHighlighted(row) ? highlightedCardRef : undefined}
              >
                <div
                  data-testid="expand-collapse"
                  className={`cursor-pointer rounded-t-lg border-x border-t px-4 pt-4 ${row.getIsExpanded() ? "border-b bg-cta-background-secondary-base pb-4" : ""}`}
                  onClick={() => toggleExpanded(row)}
                >
                  {cardHeader(row.original)}
                </div>
                {row.getIsExpanded() && (
                  <div className="border-x px-4 py-2">
                    {cardBody(row.original)}
                  </div>
                )}
                <div
                  className="flex cursor-pointer justify-center rounded-b-lg border-x border-b p-2"
                  onClick={() => toggleExpanded(row)}
                >
                  <Icon
                    icon={row.getIsExpanded() ? "arrow-up" : "arrow-down"}
                    size={8}
                  />
                </div>
              </div>
            ))
          : rows.map((row) => (
              <div
                className={`my-4 min-w-full rounded-lg border p-4 ${cellContainerClassName ?? ""} ${isRowHighlighted(row) ? "animate-row-highlight" : ""}`}
                key={row.id}
                ref={isRowHighlighted(row) ? highlightedCardRef : undefined}
              >
                {cardBody(row.original)}
              </div>
            ))
        : (emptyComponent ?? defaultEmptyState)}
      {showPagination && table.getPageCount() > 1 && (
        <>
          <div className={paginationSpacingClassName} />
          <Pagination
            table={table}
            pageSize={pageSize}
            labelForShowing={translatedContent?.paginationLabelForShowing}
            labelForOf={translatedContent?.paginationLabelForOf}
            isRightToLeft={isRtl}
            totalCount={totalCount}
          />
        </>
      )}
    </div>
  );
};
